import { Box, Stack, Typography } from '@mui/material'
import { FC, useMemo } from 'react'
import { BEIGE_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { IntegrationDTO, IntegrationStatus } from 'models/integration'

import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import { IntegrationLogo } from '../IntegrationLogo'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { useTranslation } from 'react-i18next'

export interface IntegrationCardProps {
  integration: IntegrationDTO
  onConnect: () => void
  onDisconnect: () => void
}

export const IntegrationCard: FC<IntegrationCardProps> = ({
  integration,
  onConnect,
  onDisconnect,
}) => {
  const { t } = useTranslation(['integrations'])

  const statusBadge = useMemo(() => {
    if (integration.status === IntegrationStatus.CONNECTED) {
      return (
        <MUIBadge
          label={t('integrations:status.connected')}
          color="green"
          size="sm"
          rounded
          icon={<CheckCircleOutlinedIcon />}
        />
      )
    }
    if (integration.status === IntegrationStatus.INACTIVE) {
      return (
        <MUIBadge
          label={t('integrations:status.inactive')}
          color="gray"
          size="sm"
          rounded
          icon={<BlockOutlinedIcon />}
        />
      )
    }
    return null
  }, [integration.status, t])

  const actionButton = useMemo(() => {
    if (integration.status === IntegrationStatus.NOT_CONNECTED) {
      return (
        <MUIButton type="defaultPrimary" onClick={onConnect} fullWidth>
          {t('integrations:actions.connect')}
        </MUIButton>
      )
    }

    // A synthetic Propstack binding has no BE credentials — disconnecting it would just 404
    if (integration.isSyntheticBinding) return null

    return (
      <MUIButton type="secondaryBorder" onClick={onDisconnect} fullWidth>
        {t('integrations:actions.disconnect')}
      </MUIButton>
    )
  }, [integration.status, integration.isSyntheticBinding, onConnect, onDisconnect, t])

  return (
    <BorderBoxWrapper
      elevation="xs"
      borderColor={BEIGE_600}
      sx={{
        width: '100%',
        maxWidth: '28.6rem',
        minHeight: '25.2rem',
        padding: '2.4rem',
        position: 'relative',
      }}
    >
      {statusBadge && (
        <Box
          sx={{
            position: 'absolute',
            top: '2.4rem',
            right: '2.4rem',
          }}
        >
          {statusBadge}
        </Box>
      )}

      <Stack gap="2rem" height="100%">
        <Box
          sx={{
            width: '4rem',
            height: '4rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <IntegrationLogo
            logoUrl={integration.logoUrl}
            name={t(integration.name)}
            size="md"
          />
        </Box>

        <Typography
          variant="text-xl"
          fontWeight={600}
          color={GRAY_900}
        >
          {t(integration.name)}
        </Typography>

        <Typography
          variant="text-sm"
          color={GRAY_700}
          sx={{
            lineHeight: 1.4,
            flex: 1,
          }}
        >
          {t(integration.description)}
        </Typography>

        <Box>
          {actionButton}
        </Box>
      </Stack>
    </BorderBoxWrapper>
  )
}
